import { useContext, useEffect, useState } from "react";
import { createGlobalStyle, css } from "styled-components";
import { useLocation } from "react-router";

import { useTheme } from "Hooks/useTheme";
import WebContext from "Context/WebContext";
import { ThemeColor } from "Types/ThemeContextProps";
import { useWindowScrollPositions } from "Hooks/useWindowScrollPositions";

import { ThemeTypes } from "./types/enums";
import { GetLocalState } from "Connectivity/LocalStorage";
import { PageTemplateProps } from "Types/PageProps";

// {props => props.mode} to retrieve data from the mode property
// {props => props.theme} to retrieve data from StyledProvider

/**
 * Get the background image for the active page
 * 
 * @param activePage - The active page
 * @returns The background image for the active page
 */
const getBackground = (activePage:PageTemplateProps) => {
  const assets = GetLocalState('assets');
  return assets ? activePage.background.image !== "" ? activePage.background.image : assets.images.DefaultBackground : "";
}

/**
 * Get the background size for the active page
 * 
 * @param activePage - The active page
 * @returns The background size for the active page
 */
const getBackgroundSize = (activePage:PageTemplateProps) => {
  return activePage.background.size !== "" ? activePage.background.size : "auto";
}

/**
 * Get the background repeat for the active page
 * 
 * @param activePage - The active page
 * @returns The background repeat for the active page
 */
const getBackgroundRepeat = (activePage:PageTemplateProps) => {
  return activePage.background.repeat !== "" ? activePage.background.repeat : "no-repeat";
}

/**
 * Get the background position for the active page
 * 
 * @param activePage - The active page
 * @returns The background position for the active page
 */
const getBackgroundPosition = (activePage:PageTemplateProps) => {
  return  activePage.background.position_vertical !== "" && activePage.background.position_horizontal !== ""
            ? activePage.background.position_vertical + " " + activePage.background.position_horizontal
            : "50% 0";
}

/**
 * Get the background color for the active page
 * 
 * @param activePage - The active page
 * @returns The background color for the active page
 */
const getBackgroundColor = (activePage:PageTemplateProps) => {
  return activePage.background.background_color !== "" ? activePage.background.background_color : "";
}

const GlobalStyleComponent = createGlobalStyle<{
  mode              : ThemeColor,
  scrollTop         : number,
  isMobile          : boolean,
  isGameOpen        : boolean,
  background        : string,
  hasBodyLock       : boolean,
  hasHubLock        : boolean,
  hasMegaMenuLock   : boolean,
  hasUserEnterLock  : boolean,
  hasModalLock      : boolean,
  activePage        : PageTemplateProps
}>`

  /**
   * Scroll to fullscreen when switching to landscape mode on mobile device (iOS).
   */
  html {
    @media screen and (orientation: landscape) {
      height: ${props => props.isGameOpen && props.isMobile ? "calc(100% + 10rem)" : ""};
    }
  }

  body {

    width       : 100%;
    min-height  : 100%;

    overflow-x  : hidden;
    overflow-y  : scroll;
    
    background-color          : ${props => {
                                const bgColor = getBackgroundColor(props.activePage);
                                return bgColor !== "" 
                                  ? bgColor 
                                  : props.mode === ThemeTypes.DARK 
                                    ? props.theme.body__background
                                    : props.theme.body__background
                              }};
    background-image          : url(${props => getBackground(props.activePage)});
    background-repeat         : ${props => getBackgroundRepeat(props.activePage)};
    background-size           : ${props => getBackgroundSize(props.activePage)};
    background-position       : ${props => getBackgroundPosition(props.activePage)};
    background-attachment     : fixed;

    &::-webkit-scrollbar {
      width                   : 10px;
    }

    &::-webkit-scrollbar-track {
      background              : #04112740;
    }

    &::-webkit-scrollbar-thumb {
      
      background-color        : #10233b;
      border-radius           : 20px;
      border                  : 2px solid #041127;
      
      &:hover {
        background-color      : #1d3552;
      }

    }

    ${props => {

      if ( props.hasBodyLock ) { 
        return css`
          overflow-y          : scroll;
          position            : fixed;
          left                : 0;
          top                 : -${props.isGameOpen ? 0 : props.scrollTop + "px"};
        `
      }
      else if ( props.hasModalLock || props.hasHubLock || props.hasMegaMenuLock || props.hasUserEnterLock ) {
        if ( props.isMobile ) {
          return css`
            overflow            : hidden;
          `
        }
        else {
          return css`
            background-position : calc(50% - 5px) 0!important;
            padding-right       : 10px;
            overflow            : hidden;
          `
        }
      }
      
    }}
    
  }

`;

export const GlobalStyle = () => {

  const { theme }                     = useTheme();
  const { scrollY }                   = useWindowScrollPositions();
  const location                      = useLocation();
  const [lastYScroll, SetLastYScroll] = useState<number>(-1);
  const {
    hasBodyLock,
    isGameOpen,
    isUserHubOpen,
    isUserEnterOpen,
    isModalOpen,
    isMobile,
    isMegaMenuOpen,
    activePage
  } = useContext(WebContext);

  // Otomatik scroll davranışını kapat
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; 
    }
  }, []);

  useEffect(() => {

    if ( isGameOpen ) return;
    if ( document.documentElement.scrollTop > 0 && scrollY > 0) {
      SetLastYScroll(document.documentElement.scrollTop);
    }

  }, [location, scrollY]);

  useEffect(() => {

    if ( hasBodyLock ) return;
    if ( lastYScroll === -1 ) return;

    document.documentElement.scrollTop = lastYScroll;
    SetLastYScroll(-1);

  }, [hasBodyLock]);

  return (
    <GlobalStyleComponent
      mode              = {theme as ThemeColor}
      hasModalLock      = {isModalOpen}
      hasHubLock        = {isUserHubOpen}
      hasBodyLock       = {hasBodyLock}
      hasMegaMenuLock   = {isMegaMenuOpen}
      hasUserEnterLock  = {isUserEnterOpen}
      isGameOpen        = {isGameOpen}
      scrollTop         = {lastYScroll}
      isMobile          = {isMobile}
      activePage        = {activePage}
      background        = {getBackground(activePage)}
    />
  )

}